/**
 * Widget V2 - Modèle de données unifié pour les widgets
 *
 * Architecture simplifiée avec un flux de données clair :
 * WidgetConfig → useWidgetData → API → WidgetRenderer
 */

// ============================================================================
// Types de Widgets
// ============================================================================

/**
 * Types de widgets supportés - source unique de vérité
 * Aligné sur les doc_type générés par le backend (underscore(ClassName))
 */
export const WIDGET_TYPES = {
  // Charts temporels (BucketMetricWidget)
  LINE: "line_widget",
  BAR: "bar_widget",
  AREA: "area_widget",

  // Charts catégoriels (CategoryMetricChart)
  PIE: "pie_widget",
  DONUT: "pie_widget", // Donut est un pie avec innerRadius > 0
  WORDCLOUD: "word_cloud_widget",

  // Charts spéciaux (BucketMetricWidget)
  SCATTER: "scatter_chart_widget",
  HEATMAP: "heatmap_widget",
  TREEMAP: "treemap_widget",

  // Widgets métriques (GlobalMetricWidget)
  GAUGE: "gauge_widget",
  KPI: "kpi_widget",

  // Data widgets
  TABLE: "table_widget",
  DOCUMENTS: "documents_widget",
  GRAPH: "graph_widget",
  MAP: "map_widget",

  // Static widgets
  TEXT: "text_widget",

  // KPI comparison widget
  KPI_COMPARISON: "kpi_comparison_widget",
} as const;

export type WidgetType = (typeof WIDGET_TYPES)[keyof typeof WIDGET_TYPES];

/**
 * Catégories de widgets pour le sélecteur
 */
export const WIDGET_CATEGORIES = {
  TEMPORAL: "temporal", // line, bar, area
  CATEGORICAL: "categorical", // pie, donut, wordcloud
  METRIC: "metric", // kpi, gauge
  DATA: "data", // table, documents
  VISUALIZATION: "visualization", // graph, map, heatmap, treemap, scatter
  STATIC: "static", // text
} as const;

export type WidgetCategory =
  (typeof WIDGET_CATEGORIES)[keyof typeof WIDGET_CATEGORIES];

/**
 * Métadonnées des widgets pour le sélecteur
 */
export interface WidgetMeta {
  type: WidgetType;
  name: string;
  description: string;
  icon: string;
  category: WidgetCategory;
  defaultSize: { w: number; h: number };
  requiresData: boolean;
}

export const WIDGET_REGISTRY: Record<WidgetType, WidgetMeta> = {
  [WIDGET_TYPES.LINE]: {
    type: WIDGET_TYPES.LINE,
    name: "Graphique en ligne",
    description: "Visualisez l'évolution de données dans le temps",
    icon: "📈",
    category: WIDGET_CATEGORIES.TEMPORAL,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.BAR]: {
    type: WIDGET_TYPES.BAR,
    name: "Graphique en barres",
    description: "Comparez des valeurs entre catégories",
    icon: "📊",
    category: WIDGET_CATEGORIES.TEMPORAL,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.AREA]: {
    type: WIDGET_TYPES.AREA,
    name: "Graphique en aire",
    description: "Visualisez des tendances avec remplissage",
    icon: "📉",
    category: WIDGET_CATEGORIES.TEMPORAL,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.PIE]: {
    type: WIDGET_TYPES.PIE,
    name: "Graphique circulaire",
    description: "Affichez des proportions (pie ou donut selon innerRadius)",
    icon: "🥧",
    category: WIDGET_CATEGORIES.CATEGORICAL,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  // Note: DONUT partage le même type que PIE (pie_widget)
  // La différence est dans la config visuelle (innerRadius > 0)
  [WIDGET_TYPES.SCATTER]: {
    type: WIDGET_TYPES.SCATTER,
    name: "Nuage de points",
    description: "Visualisez la corrélation entre variables",
    icon: "⚬",
    category: WIDGET_CATEGORIES.VISUALIZATION,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.HEATMAP]: {
    type: WIDGET_TYPES.HEATMAP,
    name: "Carte de chaleur",
    description: "Visualisez l'intensité sur une grille",
    icon: "🔥",
    category: WIDGET_CATEGORIES.VISUALIZATION,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.TREEMAP]: {
    type: WIDGET_TYPES.TREEMAP,
    name: "Treemap",
    description: "Hiérarchie de données en rectangles",
    icon: "🌳",
    category: WIDGET_CATEGORIES.VISUALIZATION,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.GAUGE]: {
    type: WIDGET_TYPES.GAUGE,
    name: "Jauge",
    description: "Affichez une valeur sur une échelle",
    icon: "🎯",
    category: WIDGET_CATEGORIES.METRIC,
    defaultSize: { w: 1, h: 1 },
    requiresData: true,
  },
  [WIDGET_TYPES.WORDCLOUD]: {
    type: WIDGET_TYPES.WORDCLOUD,
    name: "Nuage de mots",
    description: "Visualisez la fréquence des termes",
    icon: "☁️",
    category: WIDGET_CATEGORIES.CATEGORICAL,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.TABLE]: {
    type: WIDGET_TYPES.TABLE,
    name: "Tableau",
    description: "Affichez des données tabulaires",
    icon: "📋",
    category: WIDGET_CATEGORIES.DATA,
    defaultSize: { w: 2, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.KPI]: {
    type: WIDGET_TYPES.KPI,
    name: "Carte KPI",
    description: "Affichez un indicateur clé",
    icon: "🎯",
    category: WIDGET_CATEGORIES.METRIC,
    defaultSize: { w: 1, h: 1 },
    requiresData: true,
  },
  [WIDGET_TYPES.DOCUMENTS]: {
    type: WIDGET_TYPES.DOCUMENTS,
    name: "Documents",
    description: "Liste de documents filtrés",
    icon: "📄",
    category: WIDGET_CATEGORIES.DATA,
    defaultSize: { w: 4, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.GRAPH]: {
    type: WIDGET_TYPES.GRAPH,
    name: "Graphe relationnel",
    description: "Visualisez les relations entre entités",
    icon: "🕸️",
    category: WIDGET_CATEGORIES.VISUALIZATION,
    defaultSize: { w: 3, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.MAP]: {
    type: WIDGET_TYPES.MAP,
    name: "Carte géographique",
    description: "Visualisez des données sur une carte",
    icon: "🗺️",
    category: WIDGET_CATEGORIES.VISUALIZATION,
    defaultSize: { w: 3, h: 2 },
    requiresData: true,
  },
  [WIDGET_TYPES.TEXT]: {
    type: WIDGET_TYPES.TEXT,
    name: "Texte",
    description: "Ajoutez du texte formaté",
    icon: "📝",
    category: WIDGET_CATEGORIES.STATIC,
    defaultSize: { w: 2, h: 2 },
    requiresData: false,
  },
  [WIDGET_TYPES.KPI_COMPARISON]: {
    type: WIDGET_TYPES.KPI_COMPARISON,
    name: "Comparaison KPI",
    description: "Comparez deux valeurs KPI",
    icon: "📊",
    category: WIDGET_CATEGORIES.METRIC,
    defaultSize: { w: 1, h: 1 },
    requiresData: true,
  },
};

// ============================================================================
// Configuration des Données
// ============================================================================

/**
 * Types de métriques supportées
 */
export type MetricType =
  | "count"
  | "sum"
  | "avg"
  | "min"
  | "max"
  | "cardinality";

/**
 * Configuration d'une métrique
 */
export interface MetricConfig {
  name: string;
  type: MetricType;
  field: string | null; // null pour count
}

/**
 * Intervalles de date supportés
 */
export type DateInterval = "hour" | "day" | "week" | "month" | "year";

/**
 * Configuration de données pour widgets temporels (line, bar, area, table)
 * Utilise des buckets (groupements) avec des métriques
 */
export interface BucketDataConfig {
  kind: "bucket";
  bucketField: string;
  bucketType: "terms" | "histogram" | "date_histogram";
  metrics: MetricConfig[];
  // Options selon bucketType
  size?: number; // Pour terms (default: 10)
  dateInterval?: DateInterval; // Pour date_histogram
  numericInterval?: number; // Pour histogram
  // Split by field for multiple series (e.g., one line per tracker)
  splitBy?: string; // Champ pour séparer les données en séries multiples
  splitBySize?: number; // Nombre max de séries (default: 20)
}

/**
 * Configuration de données pour widgets catégoriels (pie, donut, wordcloud)
 * Agrégation simple sur un champ avec une métrique
 */
export interface CategoryDataConfig {
  kind: "category";
  field: string;
  metric: MetricConfig;
  size?: number; // Nombre de catégories (default: 10)
  minDocCount?: number; // Minimum de documents (default: 1)
  showMissing?: boolean;
  missingLabel?: string;
}

/**
 * Configuration de données pour widgets métriques (kpi, gauge)
 * Une seule valeur globale avec comparaison optionnelle
 */
export interface GlobalMetricDataConfig {
  kind: "global";
  metric: MetricConfig;
  // Comparaison temporelle optionnelle (pour KPI)
  comparison?: {
    period: "day" | "week" | "month" | "quarter" | "year";
    dateField: string;
  };
}

/**
 * Configuration de données pour documents/graph
 */
export interface DocumentsDataConfig {
  kind: "documents";
  size?: number;
  sort?: { field: string; order: "asc" | "desc" };
  fields?: string[];
}

/**
 * Configuration de données pour text (pas de données)
 */
export interface StaticDataConfig {
  kind: "static";
}

/**
 * Union de toutes les configurations de données
 */
export type DataConfig =
  | BucketDataConfig
  | CategoryDataConfig
  | GlobalMetricDataConfig
  | DocumentsDataConfig
  | StaticDataConfig;

// ============================================================================
// Configuration Visuelle
// ============================================================================

/**
 * Palette de couleurs
 */
export interface ColorPalette {
  colors: string[];
}

/**
 * Configuration visuelle commune à tous les widgets
 */
export interface BaseVisualConfig {
  title?: string;
  subtitle?: string;
  showLegend?: boolean;
  legendPosition?: "top" | "bottom" | "left" | "right";
  colors?: string[];
}

/**
 * Configuration visuelle pour les graphiques
 */
export interface ChartVisualConfig extends BaseVisualConfig {
  showGrid?: boolean;
  showDataLabels?: boolean;
  animation?: boolean;
  tooltips?: boolean;
  // Spécifique ligne/aire
  curveType?: "smooth" | "straight" | "stepline";
  strokeWidth?: number;
  fillOpacity?: number;
  showMarkers?: boolean;
  // Spécifique barres
  horizontal?: boolean;
  stacked?: boolean;
  // Spécifique pie/donut
  innerRadius?: number; // 0 pour pie, >0 pour donut
}

/**
 * Configuration visuelle pour KPI
 */
export interface KpiVisualConfig extends BaseVisualConfig {
  valueSize?: "small" | "medium" | "large";
  showTrend?: boolean;
  showComparison?: boolean;
  format?: {
    type: "number" | "currency" | "percentage";
    precision?: number;
    currency?: string;
    prefix?: string;
    suffix?: string;
  };
}

/**
 * Configuration visuelle pour le texte
 */
export interface TextVisualConfig extends BaseVisualConfig {
  backgroundColor?: string;
  padding?: number;
  transparentBackground?: boolean;
  blocks?: any[]; // Contenu BlockNote (array de blocks)
}

/**
 * Configuration visuelle pour le nuage de mots
 */
export interface WordCloudVisualConfig extends BaseVisualConfig {
  maxWords?: number;
  minFontSize?: number;
  maxFontSize?: number;
  rotationEnabled?: boolean;
}

/**
 * Configuration visuelle pour le tableau
 */
export interface TableVisualConfig extends BaseVisualConfig {
  striped?: boolean;
  compact?: boolean;
  showRowNumbers?: boolean;
}

/**
 * Configuration visuelle pour les documents
 */
export interface DocumentsVisualConfig extends BaseVisualConfig {
  defaultView?: "grid" | "list" | "card" | "timeline" | "masonry" | "embed";
}

/**
 * Configuration visuelle pour le graphe relationnel
 */
export interface GraphVisualConfig extends BaseVisualConfig {
  nodeSize?: number;
  linkDistance?: number;
  chargeStrength?: number;
  showLabels?: boolean;
  showTooltips?: boolean;
}

/**
 * Configuration visuelle pour la carte géographique
 */
export interface MapVisualConfig extends BaseVisualConfig {
  projection?: "mercator" | "equalEarth" | "naturalEarth";
  defaultZoom?: number;
  showMarkerLabels?: boolean;
}

/**
 * Configuration visuelle pour la jauge
 */
export interface GaugeVisualConfig extends BaseVisualConfig {
  min?: number;
  max?: number;
  thresholds?: Array<{ value: number; color: string; label?: string }>;
  showValue?: boolean;
  format?: {
    type: "number" | "currency" | "percentage";
    precision?: number;
    currency?: string;
    prefix?: string;
    suffix?: string;
  };
}

/**
 * Union des configurations visuelles
 */
export type VisualConfig =
  | ChartVisualConfig
  | KpiVisualConfig
  | TextVisualConfig
  | WordCloudVisualConfig
  | TableVisualConfig
  | DocumentsVisualConfig
  | GraphVisualConfig
  | MapVisualConfig
  | GaugeVisualConfig
  | BaseVisualConfig;

// ============================================================================
// Configuration d'Apparence
// ============================================================================

/**
 * Configuration d'apparence commune à tous les widgets
 */
export interface AppearanceConfig {
  /** Couleur de fond personnalisée (hex ou variable CSS) */
  backgroundColor?: string;
  /** Opacité du fond (0-100) */
  backgroundOpacity?: number;
  /** Afficher l'en-tête du widget */
  showHeader?: boolean;
  /** Afficher l'icône dans l'en-tête */
  showIcon?: boolean;
  /** Position de l'en-tête */
  headerPosition?: "top" | "bottom";
}

// ============================================================================
// Configuration Complète du Widget
// ============================================================================

/**
 * Filtres hiérarchiques pour un widget (similaire aux filtres de dashboard)
 */
export interface WidgetHierarchicalFilters {
  trackers?: string[] | null;
  date_range?: string | null; // "last_7_days", "last_30_days", "last_90_days", "custom"
  date_from?: string | null; // ISO format date string
  date_to?: string | null; // ISO format date string
  search?: string | null;
}

/**
 * Configuration complète d'un widget V2
 */
export interface WidgetConfig {
  id: string;
  type: WidgetType;
  name: string;
  data: DataConfig;
  visual: VisualConfig;
  /** Configuration d'apparence du widget */
  appearance?: AppearanceConfig;
  /** Si true, le widget utilise ses propres filtres indépendants du dashboard */
  independentFilters?: boolean;
  /** Filtres hiérarchiques propres au widget (utilisés si independentFilters est true) */
  filters?: WidgetHierarchicalFilters;
}

/**
 * Position et taille sur la grille
 */
export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Widget complet avec layout
 */
export interface Widget {
  id: string;
  config: WidgetConfig;
  layout: WidgetLayout;
}

// ============================================================================
// Données Transformées (Output)
// ============================================================================

/**
 * Format de données pour les graphiques (line, bar, area, pie, etc.)
 */
export interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    color?: string;
  }>;
}

/**
 * Format de données pour KPI
 */
export interface KpiData {
  value: number;
  change?: {
    value: number;
    percentage: number;
    trend: "up" | "down" | "neutral";
  };
  comparison?: {
    current: number;
    previous: number;
    period: string;
  };
}

/**
 * Format de données pour table
 */
export interface TableData {
  columns: Array<{ key: string; label: string }>;
  rows: Array<Record<string, unknown>>;
}

/**
 * Format de données pour documents
 */
export interface DocumentsData {
  documents: Array<Record<string, unknown>>;
  total: number;
}

/**
 * Union des formats de données
 */
export type WidgetData = ChartData | KpiData | TableData | DocumentsData | null;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Vérifie si un widget nécessite des données
 */
export function widgetRequiresData(type: WidgetType): boolean {
  return WIDGET_REGISTRY[type]?.requiresData ?? true;
}

/**
 * Retourne la catégorie d'un widget
 */
export function getWidgetCategory(type: WidgetType): WidgetCategory {
  return WIDGET_REGISTRY[type]?.category ?? WIDGET_CATEGORIES.DATA;
}

/**
 * Retourne les widgets par catégorie
 */
export function getWidgetsByCategory(category: WidgetCategory): WidgetMeta[] {
  return Object.values(WIDGET_REGISTRY).filter((w) => w.category === category);
}

/**
 * Crée une configuration par défaut pour un type de widget
 */
export function createDefaultWidgetConfig(
  type: WidgetType,
  id: string,
): WidgetConfig {
  const meta = WIDGET_REGISTRY[type];

  // Configuration de données par défaut selon le type
  let data: DataConfig;

  switch (type) {
    case WIDGET_TYPES.LINE:
    case WIDGET_TYPES.BAR:
    case WIDGET_TYPES.AREA:
    case WIDGET_TYPES.TABLE:
    case WIDGET_TYPES.HEATMAP:
    case WIDGET_TYPES.TREEMAP:
      data = {
        kind: "bucket",
        bucketField: "date$date",
        bucketType: "date_histogram",
        metrics: [{ name: "count", type: "count", field: null }],
        dateInterval: "month",
        size: 10,
      };
      break;

    case WIDGET_TYPES.PIE:
    // DONUT partage le même type que PIE (pie_widget)
    case WIDGET_TYPES.WORDCLOUD:
      data = {
        kind: "category",
        field: "text$type",
        metric: { name: "count", type: "count", field: null },
        size: 10,
      };
      break;

    case WIDGET_TYPES.KPI:
    case WIDGET_TYPES.KPI_COMPARISON:
    case WIDGET_TYPES.GAUGE:
      data = {
        kind: "global",
        metric: { name: "count", type: "count", field: null },
      };
      break;

    case WIDGET_TYPES.DOCUMENTS:
    case WIDGET_TYPES.GRAPH:
      data = {
        kind: "documents",
        size: 20,
      };
      break;

    case WIDGET_TYPES.MAP:
    case WIDGET_TYPES.SCATTER:
      data = {
        kind: "bucket",
        bucketField: "text$geolocation.country",
        bucketType: "terms",
        metrics: [{ name: "count", type: "count", field: null }],
        size: 100,
      };
      break;

    case WIDGET_TYPES.TEXT:
    default:
      data = { kind: "static" };
      break;
  }

  // Configuration visuelle par défaut selon le type
  // Note: On utilise des couleurs hex directes plutôt que des variables CSS
  // car les variables peuvent ne pas être définies au moment de la création
  let visual: VisualConfig;

  switch (type) {
    case WIDGET_TYPES.WORDCLOUD:
      visual = {
        title: meta?.name ?? "Widget",
        colors: [],
        maxWords: 80,
        minFontSize: 12,
        maxFontSize: 64,
        rotationEnabled: true,
      } as WordCloudVisualConfig;
      break;

    case WIDGET_TYPES.TABLE:
      visual = {
        title: meta?.name ?? "Widget",
        striped: true,
        compact: false,
        showRowNumbers: false,
      } as TableVisualConfig;
      break;

    case WIDGET_TYPES.DOCUMENTS:
      visual = {
        title: meta?.name ?? "Widget",
        defaultView: "card",
      } as DocumentsVisualConfig;
      break;

    case WIDGET_TYPES.GRAPH:
      visual = {
        title: meta?.name ?? "Widget",
        showLegend: true,
        nodeSize: 15,
        linkDistance: 30,
        chargeStrength: -200,
        showLabels: true,
        showTooltips: true,
      } as GraphVisualConfig;
      break;

    case WIDGET_TYPES.MAP:
      visual = {
        title: meta?.name ?? "Widget",
        showLegend: true,
        colors: [],
        projection: "mercator",
        defaultZoom: 1,
        showMarkerLabels: true,
      } as MapVisualConfig;
      break;

    case WIDGET_TYPES.GAUGE:
      visual = {
        title: meta?.name ?? "Widget",
        colors: [],
        min: 0,
        max: 100,
        showValue: true,
        format: { type: "number", precision: 0 },
      } as GaugeVisualConfig;
      break;

    case WIDGET_TYPES.KPI:
    case WIDGET_TYPES.KPI_COMPARISON:
      visual = {
        title: meta?.name ?? "Widget",
        colors: [],
        valueSize: "medium",
        showTrend: true,
        showComparison: type === WIDGET_TYPES.KPI_COMPARISON,
        format: { type: "number", precision: 0 },
      } as KpiVisualConfig;
      break;

    case WIDGET_TYPES.TEXT:
      visual = {
        title: "",
        transparentBackground: true,
        padding: 8,
      } as TextVisualConfig;
      break;

    case WIDGET_TYPES.PIE:
      // PIE et DONUT partagent le même type backend (pie_widget)
      // La différence est dans innerRadius (0 pour pie, >0 pour donut)
      visual = {
        title: meta?.name ?? "Widget",
        showLegend: true,
        legendPosition: "right",
        colors: [],
        showDataLabels: true,
        animation: true,
        tooltips: true,
        innerRadius: 0, // Par défaut pie standard, donut utilise innerRadius > 0
      } as ChartVisualConfig;
      break;

    default:
      // Charts temporels et autres
      visual = {
        title: meta?.name ?? "Widget",
        showLegend: true,
        legendPosition: "top",
        colors: [],
        showGrid: true,
        showDataLabels: false,
        animation: true,
        tooltips: true,
      } as ChartVisualConfig;
      break;
  }

  // Configuration d'apparence par défaut
  const appearance: AppearanceConfig = {
    showHeader: false,
    showIcon: true,
    headerPosition: "top",
    backgroundOpacity: 100,
  };

  return {
    id,
    type,
    name: meta?.name ?? "Widget",
    data,
    visual,
    appearance,
  };
}

/**
 * Convertit un type de widget vers le format backend (doc_type)
 * Les types sont unifiés, cette fonction retourne le type tel quel.
 */
export function toBackendWidgetType(type: WidgetType): string {
  return type;
}

/**
 * Convertit un type backend vers le type frontend.
 */
const SHORTHAND_TYPE_MAP: Record<string, WidgetType> = {
  documents: WIDGET_TYPES.DOCUMENTS,
  pie: WIDGET_TYPES.PIE,
  line: WIDGET_TYPES.LINE,
  bar: WIDGET_TYPES.BAR,
  area: WIDGET_TYPES.AREA,
  table: WIDGET_TYPES.TABLE,
  kpi: WIDGET_TYPES.KPI,
  gauge: WIDGET_TYPES.GAUGE,
  wordcloud: WIDGET_TYPES.WORDCLOUD,
  word_cloud: WIDGET_TYPES.WORDCLOUD,
  scatter: WIDGET_TYPES.SCATTER,
  heatmap: WIDGET_TYPES.HEATMAP,
  treemap: WIDGET_TYPES.TREEMAP,
  graph: WIDGET_TYPES.GRAPH,
  map: WIDGET_TYPES.MAP,
  text: WIDGET_TYPES.TEXT,
  kpi_comparison: WIDGET_TYPES.KPI_COMPARISON,
};

export function fromBackendWidgetType(backendType: string): WidgetType {
  const validTypes = Object.values(WIDGET_TYPES) as string[];
  if (validTypes.includes(backendType)) {
    return backendType as WidgetType;
  }
  // Handle shorthand types (e.g., "documents" -> "documents_widget")
  if (backendType in SHORTHAND_TYPE_MAP) {
    return SHORTHAND_TYPE_MAP[backendType];
  }
  return WIDGET_TYPES.LINE;
}

/**
 * Extrait le type de widget depuis un BackendWidget.
 * Le backend retourne le type dans doc_type (généré depuis le nom de la classe).
 * chart et type sont des alternatives pour la compatibilité.
 *
 * @param widget - Widget au format backend
 * @param defaultType - Type par défaut si aucun type n'est trouvé (défaut: "bar_widget")
 * @returns Le type de widget (ex: "line_widget", "bar_widget", etc.)
 */
export function getBackendWidgetType(
  widget: BackendWidget | null | undefined,
  defaultType: BackendChartType = "bar_widget",
): string {
  if (!widget) return defaultType;
  // Prioriser doc_type (ce que le backend retourne), puis chart, puis type
  return widget.doc_type || widget.chart || widget.type || defaultType;
}

// ============================================================================
// Backend Widget Types (for API communication)
// ============================================================================

/**
 * Types de widgets backend (format API)
 * Aligné sur les doc_type générés par le backend (underscore(ClassName))
 */
export type BackendChartType =
  | "line_widget"
  | "bar_widget"
  | "pie_widget"
  | "area_widget"
  | "scatter_chart_widget"
  | "gauge_widget"
  | "heatmap_widget"
  | "treemap_widget"
  | "word_cloud_widget"
  | "table_widget"
  | "documents_widget"
  | "kpi_widget"
  | "kpi_comparison_widget"
  | "map_widget"
  | "graph_widget"
  | "text_widget";

/**
 * Configuration de champ pour widget backend
 */
export interface BackendWidgetField {
  sort?: string;
  field: string;
  operator?: string;
  limit?: number;
  format?: string;
}

/**
 * Configuration de métrique backend
 */
export interface BackendMetricConfig {
  name: string;
  type: "count" | "sum" | "avg" | "min" | "max" | "cardinality";
  field: string | null;
}

/**
 * Reference to a dashboard (only contains the id)
 */
export interface DashboardRef {
  id: string;
}

/**
 * Widget au format backend (pour communication API)
 * Ce type représente le format utilisé par le backend Crowlingo
 */
export interface BackendWidget {
  id?: string;
  chart?: BackendChartType;
  name?: string | null;
  description?: string | null;
  metadata?: {
    name?: string;
    visual?: Record<string, unknown>;
    [key: string]: unknown;
  };
  queries?: Array<{
    q: Array<{
      operator: string;
      field: string;
      metadata: Record<string, unknown>;
    }>;
  }>;
  doc_type?: string;
  type?: string | null;

  // Filtrage propre au widget (Widget base)
  filter?: string | null; // Lucene query string for filtering data
  independent?: boolean; // If true, ignores parent (dashboard) filters

  // Parent dashboard reference
  dashboard?: DashboardRef | null;

  // Layout position in dashboard grid
  x?: number;
  y?: number;
  w?: number;
  h?: number;

  // Configuration pour BucketMetricWidget (area, bar, line, table, heatmap, treemap, scatter, map)
  bucket_field?: string | null;
  bucket_type?: "terms" | "histogram" | "date_histogram" | null;
  metrics?: BackendMetricConfig[];
  date_interval?: string;
  numeric_interval?: number;
  // Split by field for multiple series (e.g., one line per tracker)
  split_by?: string | null;
  split_by_size?: number;

  // Configuration pour CategoryMetricChart (pie, word_cloud)
  field?: string;
  metric?: BackendMetricConfig;
  size?: number;
  min_doc_count?: number;
  show_missing?: boolean;
  missing_label?: string;

  // Configuration pour DocumentsWidget
  from_?: number;
  sort?: Array<Record<string, unknown>>;
  fields?: string[];
  source?: boolean | string[] | Record<string, unknown>;

  // Configuration pour KpiComparisonWidget
  comparison?: {
    period: "day" | "week" | "month" | "quarter" | "year";
    metric: BackendMetricConfig;
  };
  date_field?: string;

  // Configuration pour TextWidget
  blocks?: Array<Record<string, unknown>>; // BlockNote JSON content

  // Configuration d'apparence
  appearance?: AppearanceConfig;
}

/**
 * Mode d'affichage des contenus
 */
export type ContentsDisplayMode = "normal" | "embed" | "json";

/**
 * Format type pour les widgets
 */
export type FormatType = "raw" | "ratio";

/**
 * Data for creating a new widget (backend format).
 */
export type WidgetCreate = Omit<BackendWidget, "id">;

/**
 * Data for updating an existing widget (backend format).
 */
export type WidgetUpdate = Partial<Omit<BackendWidget, "id">>;
